import '@emotion/react'
import { COLORS } from './components/GlobalStyles/constants'

type BaseThemeProps = {
  transition: string
}

interface ThemeProps extends BaseThemeProps {
  brand: {
    background: string
    foreground: string
    modalBackdrop: string
    hairline: string
    highlight: string
    neutral: string
    subtle: string
    light: string
    action: string
    actionSubtle: string
    actionLight: string
    info: string
    infoSubtle: string
    infoLight: string
    positive: string
    positiveSubtle: string
    negative: string
    negativeSubtle: string
    caution: string
    cautionSubtle: string
    cautionDark: string
    contrast: string
    iconBase: string
    iconAccent: string
  }
  card: {
    bg: string
    hairline: string
    border: string
  }
  button: {
    text: {
      primary: string
      secondary: string
      destructive: string
      link: string
    }
    bg: {
      primary: string
      secondary: string
      destructive: string
    }
    hover: {
      primary: string
      secondary: string
      destructive: string
      link: string
    }
    border: {
      primary: string
      secondary: string
      destructive: string
      link: string
    }
  }
  'choice-chip': {
    text: {
      default: string
      active: string
      disabled: string
    }
    hover: {
      default: string
    }
    bg: {
      default: string
      active: string
      disabled: string
    }
    border: {
      default: string
      active: string
      disabled: string
    }
  }
  'input-chip': {
    bg: {
      neutral: string
      negative: string
      positive: string
    }
  }
  textfield: {
    bg: {
      disabled: string
    }
    border: {
      neutral: string
      negative: string
    }
  }
  font: {
    name: string
    url: string
  }
  tabs: {
    bg: {
      default: string
      chip: string
      active: string
    }
  }
}

declare module '@emotion/react' {
  export interface Theme extends ThemeProps {}
}

const baseTheme: BaseThemeProps = {
  transition: '.2s'
}

export const lightTheme: ThemeProps = {
  ...baseTheme,
  brand: {
    background: COLORS.whistling.standard,
    foreground: COLORS.ghost,
    modalBackdrop: COLORS.bethpage.dark,
    hairline: COLORS.whistling.standard,
    highlight: COLORS.whistling.light,
    neutral: COLORS.bethpage.standard,
    subtle: COLORS.bethpage.subtle,
    light: COLORS.bethpage.light,
    action: COLORS.valhalla.standard,
    actionSubtle: COLORS.valhalla.subtle,
    actionLight: COLORS.valhalla.light,
    info: COLORS.valhalla.standard,
    infoSubtle: COLORS.valhalla.subtle,
    infoLight: COLORS.valhalla.light,
    positive: COLORS.augusta.standard,
    positiveSubtle: COLORS.augusta.subtle,
    negative: COLORS.merion.standard,
    negativeSubtle: COLORS.merion.subtle,
    caution: COLORS.dune.standard,
    cautionSubtle: COLORS.dune.subtle,
    cautionDark: COLORS.dune.dark,
    contrast: COLORS.pebble,
    iconBase: COLORS.bethpage.standard,
    iconAccent: COLORS.valhalla.standard
  },
  card: {
    bg: COLORS.pebble,
    hairline: COLORS.whistling.standard,
    border: COLORS.whistling.light
  },
  button: {
    text: {
      primary: COLORS.ghost,
      secondary: COLORS.valhalla.standard,
      destructive: COLORS.ghost,
      link: COLORS.valhalla.standard
    },
    bg: {
      primary: COLORS.valhalla.standard,
      secondary: 'transparent',
      destructive: COLORS.merion.standard
    },
    hover: {
      primary: COLORS.valhalla.hover,
      secondary: COLORS.valhalla.subtle,
      destructive: COLORS.merion.hover,
      link: COLORS.valhalla.subtle
    },
    border: {
      primary: COLORS.valhalla.standard,
      secondary: COLORS.valhalla.standard,
      destructive: COLORS.merion.standard,
      link: 'transparent'
    }
  },
  'choice-chip': {
    text: {
      default: COLORS.bethpage.light,
      active: COLORS.valhalla.standard,
      disabled: COLORS.bethpage.subtle
    },
    hover: {
      default: COLORS.valhalla.standard
    },
    bg: {
      default: COLORS.whistling.standard,
      active: COLORS.valhalla.subtle,
      disabled: COLORS.pebble
    },
    border: {
      default: COLORS.whistling.standard,
      active: COLORS.valhalla.subtle,
      disabled: COLORS.pebble
    }
  },
  'input-chip': {
    bg: {
      neutral: COLORS.valhalla.subtle,
      negative: COLORS.merion.subtle,
      positive: COLORS.augusta.subtle
    }
  },
  textfield: {
    bg: {
      disabled: COLORS.whistling.light
    },
    border: {
      neutral: COLORS.bethpage.subtle,
      negative: COLORS.merion.light
    }
  },
  font: {
    name: 'Assistant',
    url:
      'https://fonts.googleapis.com/css2?family=Assistant:wght@300;400;500;600;700;800&display=swap'
  },
  tabs: {
    bg: {
      default: 'transparent',
      chip: COLORS.whistling.light,
      active: COLORS.valhalla.subtle
    }
  }
}

export const amerilifeTheme: ThemeProps = {
  ...baseTheme,
  brand: {
    background: COLORS.coolGray.thirty,
    foreground: COLORS.ghost,
    modalBackdrop: COLORS.leadstarBlack.standard,
    hairline: COLORS.whistling.standard,
    highlight: COLORS.coolGray.ten,
    neutral: COLORS.leadstarBlack.standard,
    subtle: COLORS.coolGray.sixty,
    light: COLORS.leadstarBlack.sixty,
    action: COLORS.leadstarPurple.standard,
    actionSubtle: COLORS.leadstarPurple.ten,
    actionLight: COLORS.leadstarPurple.thirty,
    info: COLORS.valhalla.standard,
    infoSubtle: COLORS.valhalla.subtle,
    infoLight: COLORS.valhalla.light,
    positive: COLORS.augusta.standard,
    positiveSubtle: COLORS.augusta.subtle,
    negative: COLORS.merion.standard,
    negativeSubtle: COLORS.merion.subtle,
    caution: COLORS.dune.standard,
    cautionSubtle: COLORS.dune.subtle,
    cautionDark: COLORS.dune.dark,
    contrast: COLORS.pebble,
    iconBase: COLORS.leadstarBlack.standard,
    iconAccent: COLORS.leadstarPurple.standard
  },
  card: {
    bg: COLORS.coolGray.ten,
    hairline: COLORS.coolGray.standard,
    border: COLORS.coolGray.ten
  },
  button: {
    text: {
      primary: COLORS.ghost,
      secondary: COLORS.leadstarPurple.standard,
      destructive: COLORS.ghost,
      link: COLORS.leadstarPurple.standard
    },
    bg: {
      primary: COLORS.leadstarPurple.standard,
      secondary: 'transparent',
      destructive: COLORS.merion.standard
    },
    hover: {
      primary: COLORS.leadstarPurple.hover,
      secondary: COLORS.leadstarPurple.ten,
      destructive: COLORS.merion.hover,
      link: COLORS.leadstarPurple.ten
    },
    border: {
      primary: COLORS.leadstarPurple.standard,
      secondary: COLORS.leadstarPurple.standard,
      destructive: COLORS.merion.standard,
      link: 'transparent'
    }
  },
  'choice-chip': {
    text: {
      default: COLORS.leadstarBlack.sixty,
      active: COLORS.leadstarPurple.standard,
      disabled: COLORS.bethpage.subtle
    },
    hover: {
      default: COLORS.leadstarPurple.standard
    },
    bg: {
      default: COLORS.coolGray.thirty,
      active: COLORS.leadstarPurple.ten,
      disabled: COLORS.pebble
    },
    border: {
      default: COLORS.coolGray.thirty,
      active: COLORS.leadstarPurple.ten,
      disabled: COLORS.pebble
    }
  },
  'input-chip': {
    bg: {
      neutral: COLORS.leadstarPurple.ten,
      negative: COLORS.merion.subtle,
      positive: COLORS.augusta.subtle
    }
  },
  textfield: {
    bg: {
      disabled: COLORS.coolGray.ten
    },
    border: {
      neutral: COLORS.coolGray.sixty,
      negative: COLORS.merion.light
    }
  },
  font: {
    name: 'Open Sans',
    url:
      'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800&display=swap'
  },
  tabs: {
    bg: {
      default: 'transparent',
      chip: COLORS.whistling.light,
      active: COLORS.valhalla.subtle
    }
  }
}
export const darkTheme: ThemeProps = {
  ...baseTheme,
  brand: {
    background: COLORS.bethpage.dark,
    foreground: COLORS.bethpage.standard,
    modalBackdrop: COLORS.bethpage.dark,
    hairline: COLORS.whistling.standard,
    highlight: COLORS.whistling.light,
    neutral: COLORS.pebble,
    subtle: COLORS.bethpage.subtle,
    light: COLORS.bethpage.light,
    action: COLORS.valhalla.standard,
    actionSubtle: COLORS.valhalla.subtle,
    actionLight: COLORS.valhalla.light,
    info: COLORS.valhalla.standard,
    infoSubtle: COLORS.valhalla.subtle,
    infoLight: COLORS.valhalla.light,
    positive: COLORS.augusta.standard,
    positiveSubtle: COLORS.augusta.subtle,
    negative: COLORS.merion.standard,
    negativeSubtle: COLORS.merion.subtle,
    caution: COLORS.dune.standard,
    cautionSubtle: COLORS.dune.subtle,
    cautionDark: COLORS.dune.dark,
    contrast: COLORS.bethpage.standard,
    iconBase: COLORS.bethpage.standard,
    iconAccent: COLORS.valhalla.standard
  },
  card: {
    bg: COLORS.pebble[100],
    hairline: COLORS.whistling.standard,
    border: COLORS.whistling.light
  },
  button: {
    text: {
      primary: COLORS.pebble[100],
      secondary: COLORS.pebble[100],
      destructive: COLORS.pebble[100],
      link: COLORS.valhalla.standard
    },
    bg: {
      primary: COLORS.valhalla.standard,
      secondary: 'transparent',
      destructive: COLORS.merion.standard
    },
    hover: {
      primary: COLORS.valhalla['hover'],
      secondary: COLORS.whistling.light,
      destructive: COLORS.merion['hover'],
      link: COLORS.valhalla.subtle
    },
    border: {
      primary: COLORS.valhalla.standard,
      secondary: COLORS.valhalla.standard,
      destructive: COLORS.merion.standard,
      link: 'transparent'
    }
  },
  'choice-chip': {
    text: {
      default: COLORS.bethpage.light,
      active: COLORS.valhalla.standard,
      disabled: COLORS.bethpage.subtle
    },
    hover: {
      default: COLORS.valhalla.standard
    },
    bg: {
      default: COLORS.whistling.standard,
      active: COLORS.valhalla.subtle,
      disabled: COLORS.pebble[100]
    },
    border: {
      default: COLORS.whistling.standard,
      active: COLORS.valhalla.subtle,
      disabled: COLORS.pebble[100]
    }
  },
  'input-chip': {
    bg: {
      neutral: COLORS.valhalla.subtle,
      negative: COLORS.merion.subtle,
      positive: COLORS.augusta.subtle
    }
  },
  textfield: {
    bg: {
      disabled: COLORS.whistling.light
    },
    border: {
      neutral: COLORS.bethpage.subtle,
      negative: COLORS.merion.subtle
    }
  },
  font: {
    name: 'Assistant',
    url:
      'https://fonts.googleapis.com/css2?family=Assistant:wght@300;400;500;600;700;800&display=swap'
  },
  tabs: {
    bg: {
      default: 'transparent',
      chip: COLORS.whistling.light,
      active: COLORS.valhalla.subtle
    }
  }
}
